import React, { useState, useEffect } from 'react';
import { ChevronDown, LogOut, User, Settings, Bot, AlertTriangle, X, MessageCircle, BarChart3, TrendingUp, Info, Mail, Sparkles, HardDrive, Loader2 } from 'lucide-react';

/**
 * Professional Header Component
 * Features:
 * - Company logo support (logo acts as hamburger on mobile)
 * - Mobile nav dropdown: Home, Trends, Predictions, Storage, About, Contact
 * - User profile dropdown with authentication
 * - Sign out confirmation modal
 */
const NAV_TABS = [
  { id: 'home', label: 'Home', icon: BarChart3 },
  { id: 'trends', label: 'Trends', icon: TrendingUp },
  { id: 'predictions', label: 'Predictions', icon: Sparkles },
  { id: 'storage', label: 'Storage', icon: HardDrive },
  { id: 'about', label: 'About', icon: Info },
  { id: 'contact', label: 'Contact', icon: Mail },
];

const Header = ({ health, user, showChatSidebar, onToggleChat, onLogout, onProfileSettings, onPreferences, activeTab, setActiveTab }) => {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Close mobile nav when resizing to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) setMobileNavOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSignOutClick = () => {
    setUserMenuOpen(false);
    setShowSignOutModal(true);
  };

  const handleConfirmSignOut = () => {
    setShowSignOutModal(false);
    if (onLogout) {
      onLogout();
    }
  };

  const handleCancelSignOut = () => {
    setShowSignOutModal(false);
  };

  const handleBrandClick = () => {
    // Mobile: logo opens/closes the nav menu. Desktop: jump to page top.
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      setMobileNavOpen((prev) => !prev);
      return;
    }
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Use authenticated user
  const currentUser = user;

  return (
    <header className="bg-white sticky top-0 z-50" style={{ borderBottom: '1px solid var(--card-border)', boxShadow: 'var(--card-shadow)' }}>
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left: Logo and App Name (logo = hamburger trigger on mobile) */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              {/* Maintenance AI logo - on mobile this opens the nav dropdown */}
              <button
                type="button"
                onClick={handleBrandClick}
                className="flex items-center space-x-3 rounded-lg p-1 -m-1 md:p-0 md:m-0 hover:bg-gray-100 md:hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                aria-label="Open navigation menu on mobile or scroll to top on desktop"
                aria-expanded={mobileNavOpen}
                aria-haspopup="true"
              >
                <div className="h-10 w-10 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0" style={{ background: 'var(--accent-primary)' }}>
                  <Bot className="h-6 w-6 text-white" />
                </div>
                <div className="text-left">
                  <h1 className="text-lg font-semibold" style={{ color: 'var(--accent-text)' }}>
                    Maintenance AI
                  </h1>
                  <p className="hidden sm:block text-xs opacity-80" style={{ color: 'var(--accent-text-subtle)' }}>
                    Enterprise Maintenance Analytics Platform
                  </p>
                </div>
              </button>
            </div>
          </div>

          {/* Mobile nav dropdown (only when open, below md) */}
          {mobileNavOpen && (
            <>
              <div
                className="fixed inset-0 z-40 bg-black/30 md:hidden"
                onClick={() => setMobileNavOpen(false)}
                aria-hidden="true"
              />
              <div
                className="fixed left-4 right-4 top-16 z-50 md:hidden bg-white rounded-xl shadow-lg border border-gray-200 py-2 max-h-[calc(100vh-5rem)] overflow-y-auto"
                role="dialog"
                aria-label="Navigation menu"
              >
                {NAV_TABS.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      setActiveTab && setActiveTab(id);
                      setMobileNavOpen(false);
                    }}
                    className={`w-full flex items-center px-4 py-3 text-left text-sm font-medium transition-colors ${activeTab === id ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                    style={{ color: activeTab === id ? 'var(--accent-primary)' : 'var(--accent-text)' }}
                  >
                    <Icon className="h-5 w-5 mr-3 flex-shrink-0" style={{ color: activeTab === id ? 'var(--accent-primary)' : 'var(--accent-text-subtle)' }} />
                    {label}
                  </button>
                ))}
                {typeof onToggleChat === 'function' && (
                  <>
                    <div className="border-t border-gray-200 my-2" />
                    <button
                      type="button"
                      onClick={() => {
                        onToggleChat();
                        setMobileNavOpen(false);
                      }}
                      className="w-full flex items-center px-4 py-3 text-left text-sm font-medium hover:bg-gray-50 transition-colors"
                      style={{ color: 'var(--accent-text)' }}
                    >
                      <MessageCircle className="h-5 w-5 mr-3 flex-shrink-0 text-gray-500" />
                      {showChatSidebar ? 'Hide Chat' : 'Chat'}
                    </button>
                  </>
                )}
              </div>
            </>
          )}

          {/* Right: User Profile and Status */}
          <div className="flex items-center space-x-4">
            {/* System Status - Professional indicator */}
            {health?.status === 'healthy' && (
              <div className="hidden sm:flex items-center space-x-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-800 border border-emerald-100">
                <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                <span>System Operational</span>
              </div>
            )}

            {/* Chat panel toggle - left of account, aligned with profile style */}
            {typeof onToggleChat === 'function' && (
              <button
                type="button"
                onClick={onToggleChat}
                className="flex items-center justify-center min-h-[44px] min-w-[44px] sm:min-w-0 sm:px-2 sm:pr-3 py-2 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 space-x-2 sm:space-x-2.5"
                aria-label={showChatSidebar ? 'Hide chat panel' : 'Show chat panel'}
                title={showChatSidebar ? 'Hide chat' : 'Show chat'}
              >
                <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white flex-shrink-0 shadow-sm">
                  <MessageCircle className="h-4 w-4" aria-hidden strokeWidth={2.25} />
                </div>
                <span className="hidden sm:block text-left text-xs sm:text-sm font-medium text-gray-900 whitespace-nowrap">
                  {showChatSidebar ? 'Hide Chatbot' : 'Chat'}
                </span>
              </button>
            )}

            {/* User Profile Dropdown */}
            <div className="relative">
              {!currentUser ? (
                /* Loading State */
                <div className="flex items-center justify-center min-h-[44px] min-w-[44px] sm:min-w-0 sm:px-2 md:px-3 py-2 space-x-2">
                  <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
                  </div>
                  <div className="hidden sm:block text-left">
                    <div className="text-xs md:text-sm font-medium text-gray-900">Syncing...</div>
                    <div className="text-xs text-gray-500">Please wait</div>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center justify-center min-h-[44px] min-w-[44px] sm:min-w-0 sm:px-2 md:px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 space-x-2 md:space-x-3"
                >
                  <div className="h-8 w-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                    {currentUser.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div className="hidden sm:block text-left">
                    <div className="text-xs md:text-sm font-medium text-gray-900 truncate max-w-[120px] md:max-w-none">
                      {currentUser.name}
                    </div>
                    <div className="hidden md:block text-xs text-gray-500">
                      {currentUser.role}
                    </div>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform flex-shrink-0 ${userMenuOpen ? 'transform rotate-180' : ''}`} />
                </button>
              )}

              {/* Dropdown Menu */}
              {userMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setUserMenuOpen(false)}
                  ></div>
                  <div className="fixed left-4 right-4 top-16 w-[calc(100vw-2rem)] max-w-72 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-56 sm:max-w-none bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                    <div className="px-4 py-3 border-b border-gray-200">
                      <p className="text-sm font-medium text-gray-900">{currentUser.name}</p>
                      <p className="text-xs text-gray-500 truncate">{currentUser.email}</p>
                    </div>
                    <button
                      type="button"
                      className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                      onClick={() => {
                        setUserMenuOpen(false);
                        if (onProfileSettings) {
                          onProfileSettings();
                        }
                      }}
                    >
                      <User className="h-4 w-4 mr-3 text-gray-400" />
                      Profile Settings
                    </button>
                    <button
                      type="button"
                      className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                      onClick={() => {
                        setUserMenuOpen(false);
                        if (onPreferences) {
                          onPreferences();
                        }
                      }}
                    >
                      <Settings className="h-4 w-4 mr-3 text-gray-400" />
                      Preferences
                    </button>

                    <div className="border-t border-gray-200 my-1"></div>
                    <button
                      onClick={handleSignOutClick}
                      className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="h-4 w-4 mr-3" />
                      Sign Out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sign Out Confirmation Modal */}
      {showSignOutModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
            onClick={handleCancelSignOut}
          />

          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md transform transition-all border border-gray-200">
              {/* Close button */}
              <button
                onClick={handleCancelSignOut}
                className="absolute top-4 right-4 flex items-center justify-center min-h-[44px] min-w-[44px] text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>

              {/* Modal content */}
              <div className="p-6">
                {/* Icon */}
                <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-red-100 mb-4">
                  <AlertTriangle className="h-7 w-7 text-red-600" />
                </div>

                {/* Title */}
                <h3 className="text-xl font-semibold text-gray-900 text-center mb-2">
                  Sign Out
                </h3>

                {/* Message */}
                <p className="text-gray-600 text-center mb-6">
                  Are you sure you want to sign out? You'll need to sign in again to access your account.
                </p>

                {/* Buttons */}
                <div className="flex space-x-3">
                  <button
                    onClick={handleCancelSignOut}
                    className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmSignOut}
                    className="flex-1 px-4 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
